import { useTranslation } from "@sagot/i18n";
import {
  Avatar,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  toast,
} from "@sagot/ui";
import { useMembers, useTeams } from "../../../roles/use-roles";
import { useErrorMessage } from "../../../lib/api-errors";
import type { ConversationPatch } from "./inbox-api";
import type { ConversationItem } from "./inbox-model";

interface AssignMenuProps {
  conversation: ConversationItem;
  onAssign: (patch: ConversationPatch) => Promise<unknown>;
  disabled: boolean;
}

/** Assign to a member (presence-free: "You" marks the caller) or a team (round-robin). */
export function AssignMenu({ conversation, onAssign, disabled }: AssignMenuProps) {
  const { t } = useTranslation();
  const members = useMembers();
  const teams = useTeams();
  const errorMessage = useErrorMessage();
  const active = (members.data ?? []).filter((m) => m.status === "active");
  const current = conversation.assignee.type !== "ai" ? conversation.assignee.name : null;

  const assign = (patch: ConversationPatch, name: string) => {
    onAssign(patch).then(
      () => toast.success(patch.clear_assignee ? t("engage.inbox.thread.unassign") : t("engage.inbox.thread.assignedToast", { name })),
      (error: unknown) => toast.error(errorMessage(error)),
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" size="sm" disabled={disabled} data-testid="assign-menu">
          <Icon name="user-plus" />
          <span className="max-w-28 truncate">{current ?? t("engage.inbox.thread.assign")}</span>
          <Icon name="chevron-down" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>{t("engage.inbox.thread.assignTo")}</DropdownMenuLabel>
        {active.map((m) => (
          <DropdownMenuItem key={m.id} onSelect={() => { assign({ assignee_user_id: m.user_id, clear_assignee: false }, m.name); }}>
            <Avatar name={m.name} className="size-6 text-[10px]" />
            <span className="truncate">{m.name}</span>
            {m.is_you ? <span className="ml-auto text-[11px] text-fg-subtle">{t("engage.inbox.thread.you")}</span> : null}
          </DropdownMenuItem>
        ))}
        {(teams.data ?? []).length ? <DropdownMenuSeparator /> : null}
        {(teams.data ?? []).map((team) => (
          <DropdownMenuItem key={team.id} onSelect={() => { assign({ team_id: team.id, clear_assignee: false }, team.name); }}>
            <Icon name="users" className="size-4 text-fg-subtle" />
            {team.round_robin ? t("engage.inbox.thread.teamRoundRobin", { team: team.name }) : team.name}
          </DropdownMenuItem>
        ))}
        {current ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => { assign({ clear_assignee: true }, ""); }}>
              <Icon name="x" className="size-4 text-fg-subtle" />
              {t("engage.inbox.thread.unassign")}
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
